import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Form, Button, Message } from 'semantic-ui-react';
import { getFreshIdToken } from '../../utils/authUtil';
import { fetchCurrentBook } from '../../modules/book';
import { manuscriptLabel } from './aiDisclosure';
import styled from 'styled-components';
import { getTheme } from '../../constants/theme';

const palette = ({ theme }) =>
  theme.navigation || getTheme('bright').navigation;
const DisclosureCard = styled.div`
  padding: 20px;
  border-top: 1px solid ${props => palette(props).divider};
  background: ${props => palette(props).surface};
  color: ${props => palette(props).text};
  &.ui.form .field > label {
    color: inherit;
    margin-bottom: 8px;
  }
  &.ui.form .ui.selection.dropdown {
    min-height: 46px;
    border-radius: 10px;
    padding: 14px 36px 14px 14px;
    border-color: ${props => palette(props).border};
    background: ${props => palette(props).surface};
    color: ${props => palette(props).heading};
    box-shadow: none;
    transition:
      border-color 150ms,
      box-shadow 150ms;
  }
  &.ui.form .ui.selection.dropdown:focus,
  &.ui.form .ui.selection.dropdown.active {
    border-color: ${props => palette(props).accent};
    box-shadow: 0 0 0 3px ${props => palette(props).activeBackground};
  }
  &.ui.form .ui.selection.dropdown .menu {
    border-color: ${props => palette(props).border};
    border-radius: 0 0 10px 10px;
    background: ${props => palette(props).surface};
  }
  &.ui.form .ui.selection.dropdown .menu > .item {
    padding: 14px !important;
    color: ${props => palette(props).text};
    border-color: ${props => palette(props).divider};
    white-space: normal;
  }
  &.ui.form .ui.selection.dropdown .menu > .selected.item,
  &.ui.form .ui.selection.dropdown .menu > .item:hover {
    background: ${props => palette(props).activeBackground};
    color: ${props => palette(props).activeText};
  }
  &.ui.form textarea {
    border-radius: 10px;
    border-color: ${props => palette(props).border};
    background: ${props => palette(props).surface};
    color: ${props => palette(props).text};
  }
  &.ui.form .ui.checkbox label {
    color: inherit;
    line-height: 1.5;
  }
  h4 {
    color: ${props => palette(props).heading};
  }
  p {
    line-height: 1.6;
  }
  details {
    margin: 0 0 16px;
  }
  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const DisclosureList = styled.ul`
  list-style: none;
  margin: 12px 0 20px;
  padding: 0;
  border: 1px solid ${props => palette(props).border};
  border-radius: 12px;
  overflow: hidden;
  background: ${props => palette(props).surface};
`;

const DisclosureHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  h3 {
    margin: 0 0 3px;
  }
  small {
    color: ${props => palette(props).mutedText};
  }
`;

const FoldButton = styled.button`
  flex: none;
  min-height: 44px;
  padding: 8px;
  border: 0;
  background: transparent;
  color: ${props => palette(props).accent};
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${props => palette(props).accent};
    border-radius: 6px;
  }
`;

const DisclosureItem = styled.li`
  & + & {
    border-top: 1px solid ${props => palette(props).divider};
  }
`;

const VersionRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 56px;
  padding: 12px 16px;
  border: 0;
  background: transparent;
  color: ${props => palette(props).text};
  font: inherit;
  text-align: left;
  cursor: pointer;
  &:hover,
  &[aria-expanded='true'] {
    background: ${props => palette(props).activeBackground};
  }
  &:focus-visible {
    outline: 2px solid ${props => palette(props).accent};
    outline-offset: -2px;
  }
  .version-summary {
    display: block;
    margin-top: 2px;
    color: ${props => palette(props).mutedText};
    font-size: 0.9em;
  }
  .version-action {
    flex: none;
    color: ${props => palette(props).accent};
    font-weight: 600;
  }
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const emptyDisclosure = () => ({
  type: '',
  cover: 'none',
  translation: 'none',
  note: '',
});

const versionSummary = disclosure => {
  if (!disclosure?.type) return 'Not set';
  const labels = [manuscriptLabel(disclosure.type)];
  if (disclosure.type === 'generated')
    labels.push(
      disclosure.extent === 'most' ? 'Most or all text' : 'Some passages'
    );
  if (disclosure.cover === 'generated') labels.push('AI cover');
  if (disclosure.translation === 'generated') labels.push('AI translation');
  return labels.join(' · ');
};

const api = async (path, body, method = 'PUT') => {
  const token = await getFreshIdToken();
  const response = await fetch(
    `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/books/${path}`,
    {
      method,
      headers: { 'Content-Type': 'application/json', 'br-token': token },
      body: JSON.stringify(body),
    }
  );
  const data = await response.json();
  if (!response.ok)
    throw new Error(data.msg || 'Could not save AI disclosure.');
  return data;
};

function VersionDisclosure({
  book,
  version,
  reload,
  onDirtyChange,
  onSaved,
  onCancel,
}) {
  const [value, setValue] = useState(version.aiDisclosure || emptyDisclosure());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    setValue(version.aiDisclosure || emptyDisclosure());
  }, [version._id, version.aiDisclosure]);
  const update = (key, next) => {
    setValue(v => ({ ...v, [key]: next }));
    onDirtyChange(true);
  };
  const save = async () => {
    setBusy(true);
    setError('');
    let saved = false;
    try {
      await api(`${book._id}/content/${version._id}`, { aiDisclosure: value });
      await reload();
      saved = true;
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
    if (saved) onSaved(version._id);
  };
  return (
    <DisclosureCard className="ui form" id={`disclosure-editor-${version._id}`}>
      <h4>Edit version {version.versionName || version.versionNumber}</h4>
      <Form.Select
        aria-label="How was this manuscript written?"
        label="How was this manuscript written?"
        value={value.type}
        placeholder="Choose a declaration"
        options={['human', 'assisted', 'generated'].map(type => ({
          key: type,
          value: type,
          text: manuscriptLabel(type),
        }))}
        onChange={(_, d) => update('type', d.value)}
      />
      <details>
        <summary>What do these declarations mean?</summary>
        <p>
          Human-written includes spelling, grammar, dictation and accessibility
          tools. AI-assisted means you wrote the text with planning or editing
          help. Retained AI-drafted passages, even edited ones, are
          AI-generated. Clearly identified quoted AI examples are exempt.
        </p>
      </details>
      {value.type === 'generated' && (
        <Form.Select
          aria-label="How much text was generated?"
          label="How much text was generated?"
          value={value.extent}
          placeholder="Choose extent"
          options={[
            { key: 'some', value: 'some', text: 'Some passages' },
            { key: 'most', value: 'most', text: 'Most or all' },
          ]}
          onChange={(_, d) => update('extent', d.value)}
        />
      )}
      <Form.Checkbox
        label="The cover contains AI-generated artwork"
        checked={value.cover === 'generated'}
        onChange={(_, d) => update('cover', d.checked ? 'generated' : 'none')}
      />
      <Form.Checkbox
        label="This version contains an AI-generated translation"
        checked={value.translation === 'generated'}
        onChange={(_, d) =>
          update('translation', d.checked ? 'generated' : 'none')
        }
      />
      <Form.TextArea
        label="Explanation for readers (optional)"
        value={value.note || ''}
        maxLength={500}
        onChange={(_, d) => update('note', d.value)}
      />
      <Button
        color="orange"
        type="button"
        loading={busy}
        disabled={
          busy || !value.type || (value.type === 'generated' && !value.extent)
        }
        onClick={save}
      >
        Save disclosure
      </Button>
      <Button type="button" disabled={busy} onClick={onCancel}>
        Cancel
      </Button>
      {error && <Message negative>{error}</Message>}
    </DisclosureCard>
  );
}

export default function ManuscriptDisclosureEditor({ book }) {
  const dispatch = useDispatch();
  const versions = book.content || [];
  const declaredCount = versions.filter(
    version => version.aiDisclosure?.type
  ).length;
  const allDeclared = versions.length > 0 && declaredCount === versions.length;
  const [listExpanded, setListExpanded] = useState(!allDeclared);
  const [activeVersionId, setActiveVersionId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [savedVersionId, setSavedVersionId] = useState(null);
  const [foldAfterSave, setFoldAfterSave] = useState(false);
  useEffect(() => {
    if (!allDeclared) {
      setListExpanded(true);
    } else if (foldAfterSave && !dirty) {
      setListExpanded(false);
      setFoldAfterSave(false);
    }
  }, [allDeclared, foldAfterSave, dirty]);
  const reload = async () =>
    dispatch(
      fetchCurrentBook({ idToken: await getFreshIdToken(), bookId: book._id })
    );
  const chooseVersion = versionId => {
    if (dirty && !window.confirm('Discard unsaved disclosure changes?')) return;
    setActiveVersionId(activeVersionId === versionId ? null : versionId);
    setDirty(false);
    setSavedVersionId(null);
  };
  const toggleList = () => {
    if (
      listExpanded &&
      dirty &&
      !window.confirm('Discard unsaved disclosure changes?')
    )
      return;
    setListExpanded(!listExpanded);
    if (listExpanded) {
      setActiveVersionId(null);
      setDirty(false);
    }
  };
  return (
    <section>
      <DisclosureHeader>
        <div>
          <h3>AI disclosures</h3>
          <small>
            {versions.length
              ? `${declaredCount} of ${versions.length} versions declared`
              : 'No versions yet'}
            {allDeclared && savedVersionId && ' · Saved'}
          </small>
        </div>
        {allDeclared && (
          <FoldButton
            type="button"
            aria-expanded={listExpanded}
            aria-controls={`disclosure-list-${book._id}`}
            onClick={toggleList}
          >
            {listExpanded ? 'Hide versions' : 'Show versions'}{' '}
            <span aria-hidden="true">{listExpanded ? '⌃' : '⌄'}</span>
          </FoldButton>
        )}
      </DisclosureHeader>
      {listExpanded && (
        <p>
          Declare each version before sharing it.{' '}
          <a
            href="https://betareader.io/community-guidelines"
            target="_blank"
            rel="noopener noreferrer"
          >
            Definitions and examples
          </a>
        </p>
      )}
      <DisclosureList id={`disclosure-list-${book._id}`} hidden={!listExpanded}>
        {versions.map(version => {
          const open = activeVersionId === version._id;
          const name = `Version ${version.versionName || version.versionNumber}`;
          return (
            <DisclosureItem key={version._id}>
              <VersionRow
                type="button"
                aria-expanded={open}
                aria-controls={
                  open ? `disclosure-editor-${version._id}` : undefined
                }
                onClick={() => chooseVersion(version._id)}
              >
                <span>
                  <strong>{name}</strong>
                  <span className="version-summary">
                    {versionSummary(version.aiDisclosure)}
                    {savedVersionId === version._id && ' · Saved'}
                  </span>
                </span>
                <span className="version-action">
                  {open ? 'Close' : version.aiDisclosure?.type ? 'Edit' : 'Add'}
                  {' ›'}
                </span>
              </VersionRow>
              {open && (
                <VersionDisclosure
                  book={book}
                  version={version}
                  reload={reload}
                  onDirtyChange={setDirty}
                  onSaved={id => {
                    setActiveVersionId(null);
                    setDirty(false);
                    setSavedVersionId(id);
                    setFoldAfterSave(true);
                  }}
                  onCancel={() => chooseVersion(version._id)}
                />
              )}
            </DisclosureItem>
          );
        })}
      </DisclosureList>
      <VisuallyHidden role="status" aria-live="polite">
        {savedVersionId && 'Disclosure saved.'}
      </VisuallyHidden>
    </section>
  );
}
